/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, Input, OnInit} from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import {DateTime as LuxonDateTime} from 'luxon';

import template from './date-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {TargetingTemplateHook} from './targeting-template.component';
import {TargetingHelper} from '../targeting-helper';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';

@Component({
  standalone: false,
  selector: 'adn-date-targeting',
  template
})
export class DateTargetingComponent implements OnInit {
  @Input() model: any;
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.date;
  @Input() underModel: any;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;
  @Input() lineItemStartDate: string;

  vm: any = {dateRanges: []};

  private readonly apiFormat = 'YYYY-MM-DDTHH:mm';

  constructor(
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any
  ) {
  }

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      this.prepareForSave();
      return {
        vm: _.cloneDeep(this.vm),
        model: _.cloneDeep(this.model),
        constant: ADN_TARGETING_TYPES.date,
        summary: ''
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  ngOnInit(): void {
    this.ensureModel();
    this.initFromModel();

    if (!this.tabController) {
      return;
    }

    const prepareForSave = () => this.prepareForSave();
    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.register(ADN_TARGETING_TYPES.date.widget, prepareForSave, copyTemplate);
    this.tabController.callBeforeSubmit(prepareForSave);
    this.tabController.registerAllTabs(prepareForSave);
  }

  addDate(): void {
    const times = this.createTimeTemplate();
    this.vm.dateRanges.push({
      entireDay: true,
      start: times.first,
      end: times.second
    });
  }

  remove(index: number): void {
    _.pullAt(this.vm.dateRanges, index);
  }

  private ensureModel(): void {
    if (!this.model) {
      return;
    }
    this.model.dateRanges = this.model.dateRanges || [];
    this.model.timeZoneSetting = this.model.timeZoneSetting || 'USER';
  }

  private createTimeTemplate(d?: any): {first: Date; second: Date} {
    const dateToUse = this.getDefaultDateTime();
    return {
      first: d && d.first ? this.toStoredDate(d.first) : this.toUtcDisplayDate(dateToUse.startOf('day')),
      second: d && d.second ? this.toStoredDate(d.second) : this.toUtcDisplayDate(dateToUse.set({hour: 23, minute: 59, second: 0, millisecond: 0}))
    };
  }

  private initFromModel(): void {
    this.vm = {dateRanges: _.cloneDeep(_.get(this.model, 'dateRanges') || [])};
    _.forEach(this.vm.dateRanges, (d: any) => {
      const entireDay = TargetingHelper.isEntireDay(d);
      const times = this.createTimeTemplate(d);
      d.entireDay = entireDay;
      if (entireDay) {
        d.start = this.toStoredDate(d.first);
        d.end = this.toStoredDate(d.second);
      } else {
        d.start = times.first;
        d.end = times.second;
      }
      delete d.first;
      delete d.second;
    });
  }

  private completeApiDay(date: Date): {first: string; second: string} {
    const utcDate = LuxonDateTime.fromJSDate(date).setZone('utc');
    return {
      first: moment.utc(utcDate.startOf('day').toJSDate()).format(this.apiFormat),
      second: moment.utc(utcDate.startOf('day').plus({days: 1}).toJSDate()).format(this.apiFormat)
    };
  }

  private prepareForSave(): void {
    if (!this.model) {
      return;
    }
    const formattedForSave = [];
    _.forEach(this.vm.dateRanges, (d: any) => {
      if (!d.start || !moment(d.start).isValid()) {
        return;
      }
      if (d.entireDay) {
        formattedForSave.push(this.completeApiDay(d.start));
        return;
      }
      if (!d.end || !moment(d.end).isValid()) {
        return;
      }
      if (moment(d.start).isAfter(moment(d.end))) {
        return;
      }
      formattedForSave.push({
        first: moment.utc(d.start).format(this.apiFormat),
        second: moment.utc(d.end).format(this.apiFormat)
      });
    });
    this.model.dateRanges = formattedForSave;
  }

  private getDefaultDateTime(): LuxonDateTime {
    const networkTimeZone = this.LocalNetworkProfile.get('timeZone') || 'utc';
    const now = LuxonDateTime.now().setZone(networkTimeZone);
    const lineItemStartDate = this.parseLineItemStartDate(networkTimeZone);

    if (lineItemStartDate && lineItemStartDate.toMillis() > now.toMillis()) {
      return lineItemStartDate;
    }

    return now;
  }

  private parseLineItemStartDate(networkTimeZone: string): LuxonDateTime | null {
    if (!this.lineItemStartDate) {
      return null;
    }

    const parsed = LuxonDateTime.fromISO(this.lineItemStartDate, {setZone: true});
    if (!parsed.isValid) {
      return null;
    }

    return parsed.setZone(networkTimeZone);
  }

  private toStoredDate(value: Date | string): Date {
    if (value instanceof Date) {
      return new Date(value);
    }

    const parsedIso = LuxonDateTime.fromISO(value, {zone: 'utc'});
    if (parsedIso.isValid) {
      return parsedIso.toJSDate();
    }

    const parsedSql = LuxonDateTime.fromSQL(value, {zone: 'utc'});
    return parsedSql.isValid ? parsedSql.toJSDate() : new Date(NaN);
  }

  private toUtcDisplayDate(value: LuxonDateTime): Date {
    return LuxonDateTime.utc(
      value.year,
      value.month,
      value.day,
      value.hour,
      value.minute,
      0,
      0
    ).toJSDate();
  }

  private copyTemplate(tpl: any): void {
    if (!this.model) {
      return;
    }
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || {dateRanges: []};
    Object.keys(this.model).forEach((key) => delete this.model[key]);
    Object.assign(this.model, copied);
    this.ensureModel();
    this.initFromModel();
  }
}
